import { connect } from 'redux-vue';
import Validator from 'validatorjs';
import classNames from 'classnames';
import * as userActions from '../actions/user';
import PasswordInput from "../components/PasswordInput";
import LoadingIndicator from '../components/LoadingIndicator';
import ModalTemplate, {modalPageDecorator} from './ModalTemplate';
import ValidatedComponent from '../components/ValidatedComponent';

const LoginModal = modalPageDecorator({
  props: ['user', 'login'],

  data() {
    return {
      formData: {
        email: "",
        password: ""
      },
      formRules: {
        email: "required|email",
        password: "required"
      },
      formErrors: null,
      formLabels: {
        email: this.$t('login_modal.email_label'),
        password: this.$t('login_modal.password_label')
      },
    }
  },

  methods: {
    /**
     *
     * @param event
     * @returns {Promise<void>}
     */
    async onSubmitPress (event) {
      event.preventDefault();

      const validation = new Validator(this.formData, this.formRules);

      validation.setAttributeFormatter(attr => this.formLabels[attr].toLowerCase());

      if (validation.passes()) {
        this.formErrors = null;
        await this.login(this.formData.email, this.formData.password);
        if (this.user.loginSuccess) {
          const nextPath = this.$route.query.redirect || 'CoursesPage';
          if (this.$route.name !== nextPath) {
            this.$router.replace({name: nextPath});
          }
        } else if (this.user.loginError) {
          this.formErrors = this.user.loginError.response.data.errors;
        }
      } else {
        this.formErrors = validation.errors.all();
      }

    }
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    const submitClass = classNames(
      "flex-shrink-0 mr-4 lg:mr-8 px-5 p-2 lg:p-3 rounded-full font-serif bg-yellow-500 focus:outline-none focus:shadow-outline text-base",
      {'cursor-wait': this.user.login}
    );

    return (
      <ModalTemplate>
        <div class="m-2 lg:my-12">
          <div class="bg-white container md:max-w-xl mx-auto rounded shadow-lg relative z-50">

            <div class="absolute top-0 right-0 cursor-pointer flex flex-col items-center mt-4 mr-4 text-white text-sm z-50">
            </div>

            <div class="pb-6 p-3 lg:px-10">
              <div onClick={this.dismiss} class="cursor-pointer z-50 absolute right-0 top-0 m-3 lg:m-4">
                <i class="mdi mdi-close text-3xl lg:text-4xl leading-none text-shadow"></i>
              </div>
              <h3 class="text-base lg:text-xl font-bold lg:mt-5 mb-8 lg:mb-4">
                {this.$t('login_modal.h1')}
              </h3>

              <form action="#">
                <div class="lg:w-10/12">
                  <div class="mb-4">
                    <label class="block text-gray-700 text-sm mb-2" htmlFor="email">
                      {this.formLabels.email}
                    </label>
                    <ValidatedComponent
                      name="email"
                      errors={this.formErrors}
                      renderComponent={cn => (
                        <input
                          class={"bg-gray-100 text-sm appearance-none border border-faded-200 rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:shadow-outline " + cn}
                          id="email" type="email" v-model={this.formData.email}/>
                      )}
                    />
                  </div>
                  <div class="mb-4">
                    <label class="block text-gray-700 text-sm mb-2" htmlFor="password">
                      {this.formLabels.password}
                    </label>
                    <ValidatedComponent
                      name="password"
                      errors={this.formErrors}
                      activeClassname="border-red-500"
                      inactiveClassname="border-faded-200"
                      renderComponent={cn => (
                        <div>
                          <PasswordInput
                            v-model={this.formData.password}
                            classname={cn}
                          />
                          <a href="https://watch.immunizationacademy.com/en/password/reset" target="_blank" class="float-right text-teal-500 text-xs lg:text-sm pt-1">{this.$t('login_modal.forgot_password')}</a>
                        </div>
                      )}
                    />
                  </div>
                  <div class="flex items-center pt-5">
                    <button class={submitClass} type="submit" onClick={this.onSubmitPress} disabled={this.user.login}>
                      {this.user.login && <LoadingIndicator />}
                      {this.user.login || (
                        <fragment>
                          {this.$t('sign_in')}
                          <i class="mdi mdi-arrow-right ml-1"></i>
                        </fragment>
                      )}
                    </button>
                    <router-link to={{name: "RegisterModal"}} replace class="text-xs lg:text-sm whitespace-no-wrap text-teal-500">
                      {this.$t('login_modal.or_register_an_account')}
                    </router-link>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalTemplate>
    );
  }
});

function mapStateToProps(state) {
  return {
    user: state.user
  };
}

function mapActionToProps(dispatch) {
  return {
    login: async (email, password) => await dispatch(userActions.login(email, password)),
  }
}

export default connect(mapStateToProps, mapActionToProps)(LoginModal);
